import { useState, useRef, memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  FormControl,
  MenuItem,
  Popover,
  Stack,
  ButtonBase,
  TextField,
  Select,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { SelectRenderValueElem } from '../SelectDropdown/SelectRenderValueElem';
import { DropdownHeader } from '../DropdownHeader';
import { getCommonPopoutSx } from 'utility/styles';
import { getSelectSx, getCloseButtonSx, getFooterContainerSx } from '../dropdownConsts';
import { bloxValues } from 'themes/constants';

type EnterTextDropdownInputProps = {
  label?: string;
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  numeric?: boolean;
  textArea?: boolean;
};

export const EnterTextDropdownInput = memo(
  ({
    label = '',
    value,
    onChange,
    placeholder = '',
    disabled = false,
    numeric = false,
    textArea = false,
  }: EnterTextDropdownInputProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const anchorRef = useRef<HTMLElement | null>(null);
    const [textValue, setTextValue] = useState<string>(value || '');
    const isDirty = textValue.trim() !== (value ?? '');

    useEffect(() => {
      if (open) {
        setTextValue(value ?? '');
      }
    }, [value, open]);

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const selectedChoices =
      value === ''
        ? [{ label: t('blox.actions.remove'), value: '' }]
        : value
          ? [{ label: value, value }]
          : [];

    return (
      <FormControl
        fullWidth
        size="small"
        variant="outlined"
        disabled={disabled}
        sx={{ m: 0 }}
      >
        <Select
          data-testid="enter-text-dropdown-select"
          ref={anchorRef}
          value="selected"
          displayEmpty
          onOpen={handleOpen}
          open={open}
          MenuProps={{ open: false }}
          renderValue={() => (
            <SelectRenderValueElem
              selectedChoices={selectedChoices}
              placeholderText={placeholder}
              renderValueDisplay="chips"
              chipColor={value === '' ? 'error' : 'success'}
            />
          )}
          sx={getSelectSx}
        >
          <MenuItem
            value="selected"
            style={{ display: 'none' }}
          />
        </Select>

        <Popover
          anchorEl={anchorRef.current}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{
            paper: {
              sx: {
                display: 'grid',
                gridTemplateRows: 'auto 1fr auto',
                gridTemplateAreas: `'selectheader' 'selectmainarea' 'selectfooter'`,
                minWidth: '200px',
                container: 'selectgrid / normal',
                overflow: 'hidden',
                p: 0,
                ...getCommonPopoutSx(theme),
              },
            },
          }}
        >
          <Box
            sx={{
              p: 0,
              minWidth: 300,
            }}
          >
            <DropdownHeader
              label={label}
              disabled={disabled}
              valueIsEmpty={value !== ''}
              onRemove={() => {
                onChange('');
                handleClose();
              }}
              alwaysShowRemove={true}
            />

            <Stack
              spacing={1}
              sx={{
                p: 1,
              }}
            >
              <TextField
                data-testid="enter-text-dropdown-input"
                size="small"
                variant="outlined"
                fullWidth
                disabled={disabled}
                placeholder={placeholder}
                type={numeric ? 'number' : 'text'}
                multiline={textArea}
                minRows={textArea ? 3 : undefined}
                value={textValue}
                onChange={(e) => setTextValue(e.target.value)}
                sx={{
                  '& .MuiInputBase-root, & .MuiFormControlLabel-label': {
                    fontSize: bloxValues.smallFontSizeRem,
                  },
                }}
              />
            </Stack>
            <Stack
              sx={{
                gridArea: 'selectfooter',
                display: 'grid',
                gridTemplateColumns: 'auto auto',
                gridTemplateAreas: `"updatebtn closebtn"`,
                justifyContent: 'end',
                alignItems: 'center',
                ...getFooterContainerSx(theme, true),
              }}
            >
              <Button
                data-testid="enter-text-dropdown-update-btn"
                size="small"
                variant="contained"
                onClick={() => {
                  if (textValue.trim() === '') {
                    onChange(null);
                  } else {
                    onChange(textValue.trim());
                  }
                  handleClose();
                }}
                disabled={textValue.trim() === '' && value === null}
                color={isDirty ? 'primary' : 'success'}
                sx={{
                  gridArea: 'updatebtn',
                  color: theme.palette.common.white,
                  borderRadius: 0,
                  px: 2,
                  height: '100%',
                }}
                disableElevation
              >
                {textValue.trim() === value && value !== null
                  ? t('blox.actions.clear')
                  : t('blox.actions.update')}
              </Button>
              <ButtonBase
                data-testid="enter-text-dropdown-close-btn"
                onClick={handleClose}
                sx={{
                  gridArea: 'closebtn',
                  ...getCloseButtonSx(theme),
                }}
              >
                {t('blox.actions.close')}
              </ButtonBase>
            </Stack>
          </Box>
        </Popover>
      </FormControl>
    );
  },
);

EnterTextDropdownInput.displayName = 'EnterTextDropdownInput';
